(() => {
  const endpoint = 'https://pathosion-form-notification-relay.growth-ops.workers.dev/v1/contact';
  const createRequestId = () => {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
  };
  const forms = [...document.querySelectorAll('[data-contact-form]')].filter((form) => !form.dataset.initialized);
  if (!forms.length) return;
  const initialize = (form) => {
    form.dataset.initialized = 'true';
    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-contact-status]');
    const documentKey = ['ec', 'sns', 'ai'].includes(form.dataset.documentKey) ? form.dataset.documentKey : '';
    const submitLabel = documentKey ? '送信してダウンロード' : '送信する';
    let widgetId;
    let token = '';
    let busy = false;
    let completed = false;
    let lastPayload = '';
    let requestId;
    let step = 1;
    const stepped = form.dataset.multistep === 'true';
    const panels = stepped ? [...form.querySelectorAll('[data-contact-step]')] : [];
    const next = stepped ? form.querySelector('[data-contact-next]') : null;
    const back = stepped ? form.querySelector('[data-contact-back]') : null;
    const showStep = (value, focus = true) => {
      step = value;
      panels.forEach((panel) => { panel.hidden = Number(panel.dataset.contactStep) !== step; });
      form.querySelectorAll('[data-step-indicator]').forEach((indicator) => {
        if (Number(indicator.dataset.stepIndicator) === step) indicator.setAttribute('aria-current', 'step');
        else indicator.removeAttribute('aria-current');
      });
      back.hidden = step === 1;
      next.hidden = step === 3;
      button.hidden = step !== 3;
      if (focus) panels.find((panel) => !panel.hidden)?.querySelector('input, textarea')?.focus();
    };
    const validateStep = () => {
      for (const panel of panels.filter((panel) => !panel.hidden)) {
        for (const field of panel.querySelectorAll('input, textarea')) {
          if (!field.reportValidity()) return false;
        }
      }
      return true;
    };
    if (stepped) {
      form.noValidate = true;
      form.querySelector('[data-contact-progress]').hidden = false;
      form.querySelector('[data-contact-navigation]').hidden = false;
      next.addEventListener('click', () => { if (!busy && !completed && validateStep()) showStep(step + 1); });
      back.addEventListener('click', () => { if (!busy && !completed) showStep(step - 1); });
      showStep(1, false);
    }
    const syncButton = () => { button.disabled = busy || completed || !token; };
    const verificationFailed = () => {
      token = '';
      syncButton();
      if (!completed && !busy) status.textContent = '送信前の安全確認を完了できませんでした。通信環境をご確認のうえ、ページを再読み込みしてください。';
    };
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (stepped && step < 3) {
        if (!busy && !completed && validateStep()) showStep(step + 1);
        return;
      }
      if (stepped) {
        for (const panel of panels) {
          const invalid = [...panel.querySelectorAll('input, textarea')].find((field) => !field.checkValidity());
          if (invalid) { showStep(Number(panel.dataset.contactStep), false); invalid.reportValidity(); return; }
        }
      }
      if (busy || completed || !token || !form.reportValidity()) return;
      const data = new FormData(form);
      const payload = Object.fromEntries(['name', 'email', 'phone', 'company', 'department', 'jobTitle', 'inquiryContent'].map((key) => [key, String(data.get(key) || '').trim()]));
      payload.inquiryTypes = data.getAll('inquiryTypes').sort();
      payload.sourcePath = window.location?.pathname || '/pages/contact';
      payload.pageTitle = form.dataset.sourceLabel || document.title || 'Pathosionお問い合わせ';
      const fingerprint = JSON.stringify(payload);
      // Keep the same receipt on retries after a network failure, but never
      // attach changed content to an earlier submission's identity.
      busy = true;
      syncButton();
      form.setAttribute('aria-busy', 'true');
      button.textContent = '送信しています…';
      status.textContent = '';
      let failureMessage = '送信を確認できませんでした。入力内容は保持していますので、通信環境をご確認のうえ、再度お試しください。';
      let cancelTimeout = () => {};
      try {
        if (!requestId || lastPayload !== fingerprint) requestId = createRequestId();
        lastPayload = fingerprint;
        let signal;
        if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
          signal = AbortSignal.timeout(60000);
        } else if (typeof AbortController !== 'undefined') {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 60000);
          signal = controller.signal;
          cancelTimeout = () => clearTimeout(timeoutId);
        }
        const response = await fetch(endpoint, {
          method: 'POST', credentials: 'omit', redirect: 'error',
          headers: { 'Content-Type': 'application/json' },
          ...(signal ? { signal } : {}),
          body: JSON.stringify({ ...payload, requestId, 'cf-turnstile-response': token }),
        });
        const result = await response.json();
        if (!response.ok || result.accepted !== true || result.receipt !== requestId) {
          const errors = {
            verification_failed: '安全確認の有効期限が切れました。確認完了後、もう一度送信してください。',
            rate_limited: '短時間に送信が集中しています。少し時間を置いてからお試しください。',
            invalid_fields: '入力内容をご確認ください。電話番号には数字とハイフンをご使用ください。',
          };
          failureMessage = errors[result.error] || failureMessage;
          throw new Error('submission_failed');
        }
        completed = true;
        if (documentKey) window.location.assign(`/pages/thanks?material=${encodeURIComponent(documentKey)}`);
        status.textContent = 'お問い合わせを受け付けました。内容を確認後、担当者よりご連絡いたします。';
        form.querySelectorAll('input, textarea').forEach((field) => { field.disabled = true; });
      } catch {
        status.textContent = failureMessage;
      } finally {
        cancelTimeout();
        busy = false;
        token = '';
        form.removeAttribute('aria-busy');
        button.textContent = completed ? '送信済み' : submitLabel;
        syncButton();
        // A token is single-use even when storage or the network fails.
        if (widgetId !== undefined) {
          if (completed) window.turnstile.remove(widgetId);
          else window.turnstile.reset(widgetId);
        }
        status.focus();
      }
    });
    return () => {
      if (!form.dataset.sitekey || form.action !== endpoint) return verificationFailed();
      try {
        widgetId = window.turnstile.render(form.querySelector('[data-contact-turnstile]'), {
          sitekey: form.dataset.sitekey, action: 'contact', theme: 'light', language: 'ja',
          appearance: 'interaction-only',
          callback: (value) => { token = value; syncButton(); },
          'expired-callback': () => { token = ''; syncButton(); },
          'error-callback': verificationFailed,
          'timeout-callback': verificationFailed,
        });
      } catch { verificationFailed(); }
    };
  };
  const renderers = forms.map(initialize);
  if (window.turnstile) renderers.forEach((render) => render());
  else {
    window.pathosionContactTurnstileReady = () => renderers.forEach((render) => render());
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=pathosionContactTurnstileReady&render=explicit';
    script.async = true;
    script.onerror = () => forms.forEach((form) => {
      form.querySelector('[data-contact-status]').textContent = '安全確認を読み込めませんでした。通信環境をご確認のうえ、ページを再読み込みしてください。';
    });
    document.head.appendChild(script);
  }
})();
